import { useEffect, useState } from "react";

/**
 * Inline maths preview. KaTeX is loaded on first use, so screens without maths
 * never fetch it. The bank's `\(…\)` and `\[…\]` are maths, as in the student
 * app; so is `$…$`, which hand-written JSON imports still use. Everything else
 * is plain text.
 */
interface Katex {
  renderToString(tex: string, options?: { throwOnError?: boolean; displayMode?: boolean }): string;
}

let katex: Katex | null = null;
let loading: Promise<Katex | null> | null = null;

/** KaTeX, loaded once; null when it couldn't be fetched. */
export function loadKatex(): Promise<Katex | null> {
  if (katex) return Promise.resolve(katex);
  loading ??= Promise.all([import("katex"), import("katex/dist/katex.min.css")])
    .then(([module]) => (katex = (module.default ?? module) as unknown as Katex))
    .catch(() => {
      loading = null;
      return null;
    });
  return loading;
}

const escape = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** `\(x\)` → `$x$` and `\[x\]` → `$x$`, so one splitter handles every form. */
const unify = (text: string) =>
  text.replace(
    /\\\((.+?)\\\)|\\\[(.+?)\\\]/gs,
    (_m, inline?: string, display?: string) => `$${inline ?? display}$`,
  );

/** Split on unescaped `$`. An odd count means an unclosed formula. */
export function renderMath(raw: string, engine: Katex | null): { html: string; ok: boolean } {
  const text = unify(raw);
  const parts = text.split(/(?<!\\)\$/);
  if (parts.length % 2 === 0) return { html: escape(text), ok: false };
  let ok = true;
  const html = parts
    .map((part, index) => {
      if (index % 2 === 0) return escape(part).replace(/\n/g, "<br/>");
      if (!engine) return `<code>${escape(part)}</code>`;
      try {
        return engine.renderToString(part, { throwOnError: true });
      } catch {
        ok = false;
        return `<span class="text-negative">${escape(`$${part}$`)}</span>`;
      }
    })
    .join("");
  return { html, ok };
}

export function MathText({ text, className = "" }: { text: string; className?: string }) {
  const [engine, setEngine] = useState<Katex | null>(katex);
  useEffect(() => {
    if (!engine && /\$|\\\(|\\\[/.test(text)) void loadKatex().then(setEngine);
  }, [engine, text]);
  const { html } = renderMath(text, engine);
  // The HTML is KaTeX output or escaped text — never raw input.
  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}
